import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, LockKey, Notebook, UsersThree } from "@phosphor-icons/react";
import MarketingLayout, { PageMeta } from "@/components/MarketingLayout";

export default function About() {
  return (
    <MarketingLayout>
      <PageMeta
        title="About | Nothing Before Law"
        description="Why Nothing Before Law is building a focused case diary for Indian advocates and legal teams."
      />

      <section className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-14 sm:px-6 md:grid-cols-[0.88fr_1.12fr] lg:gap-16 lg:px-8 lg:py-20">
        <div className="max-w-xl">
          <p className="marketing-eyebrow">About Nothing Before Law</p>
          <h1 className="mt-5 font-serif text-5xl leading-[1.08] tracking-[-0.025em] text-[var(--m-text)] sm:text-6xl">
            Built around an advocate's working day.
          </h1>
          <p className="mt-6 text-lg leading-7 text-[var(--m-muted)]">
            Legal work deserves software that respects the matter, the client and the discipline behind every court appearance.
          </p>
        </div>
        <div className="marketing-image-frame">
          <img
            src="/images/marketing/advocates-entering-court.webp"
            alt="Indian advocates arriving at a district court with organised case files"
            width="1448"
            height="1086"
            fetchPriority="high"
            className="aspect-[4/3] w-full object-cover"
          />
        </div>
      </section>

      <section className="marketing-section marketing-tinted-section">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 md:grid-cols-[0.8fr_1.2fr] lg:gap-20 lg:px-8">
          <h2 className="font-serif text-4xl leading-tight text-[var(--m-text)] sm:text-5xl">The idea is simple.</h2>
          <div className="max-w-2xl space-y-5 text-lg leading-8 text-[var(--m-muted)]">
            <p>A case diary should be more than a list of dates. It should preserve the working memory of a practice.</p>
            <p>Nothing Before Law brings matters, clients, hearings, tasks, documents and accounts into one connected record.</p>
            <p>The result is less time reconstructing yesterday and more time preparing the work that matters next.</p>
          </div>
        </div>
      </section>

      <section className="marketing-section mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <h2 className="font-serif text-4xl leading-tight text-[var(--m-text)] sm:text-5xl">What guides the product.</h2>
        </div>
        <div className="mt-10 grid gap-x-12 gap-y-10 md:grid-cols-2">
          {[
            [Notebook, "The matter comes first", "Every feature should help an advocate understand, prepare or account for a matter."],
            [Eye, "Clarity beats complexity", "Important dates, ownership and money should be easy to read without specialist setup."],
            [LockKey, "Privacy is part of the workflow", "Sessions, PIN locking and firm-scoped records protect daily access to sensitive work."],
            [UsersThree, "Solo today, a team tomorrow", "The same diary should support an independent advocate and a growing chamber."],
          ].map(([Icon, title, body]) => (
            <article key={title} className="marketing-principle">
              <Icon size={28} weight="duotone" aria-hidden="true" />
              <h3 className="mt-5 font-serif text-2xl text-[var(--m-text)]">{title}</h3>
              <p className="mt-3 text-sm leading-6 text-[var(--m-muted)]">{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="marketing-section mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 md:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:px-8">
        <div className="marketing-image-frame">
          <img
            src="/images/marketing/advocate-chambers.webp"
            alt="An Indian advocate preparing files and hearing dates in her chambers"
            width="1448"
            height="1086"
            loading="lazy"
            className="aspect-[4/3] w-full object-cover"
          />
        </div>
        <div>
          <h2 className="font-serif text-4xl leading-tight text-[var(--m-text)] sm:text-5xl">A quieter way to stay prepared.</h2>
          <p className="mt-5 text-lg leading-7 text-[var(--m-muted)]">
            The product is designed to feel composed under pressure, with the day's work visible and the next action clear.
          </p>
          <Link to="/login?mode=register" className="marketing-text-link mt-7 inline-flex items-center gap-2">
            Create account <ArrowRight size={17} weight="bold" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
